import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  H1Plugin,
  H2Plugin,
  H3Plugin,
  BlockquotePlugin,
  HorizontalRulePlugin,
} from "@platejs/basic-nodes/react";
import { TablePlugin } from "@platejs/table/react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Table as TableIcon,
  Undo2,
  Redo2,
  Minus,
  Plus,
  Type,
  Quote,
  ChevronDown,
  Trash2,
  RowsIcon,
  Columns3,
  PlusSquare,
} from "lucide-react";
import {
  TextAlignPlugin,
  FontSizePlugin,
} from "./plugins";

// ─── Helpers ─────────────────────────────────────────────────────────────────
// The toolbar lives OUTSIDE the Plate context (it's in the outer chrome strip),
// so we cannot use useEditorRef / useEditorSelector. Instead we subscribe to
// the editor's Slate onChange to keep mark / block state in sync.

type AnyEditor = {
  marks?: Record<string, unknown>;
  selection?: unknown;
  undo: () => void;
  redo: () => void;
  nodes: (opts: object) => IterableIterator<[unknown, unknown]>;
  isBlock: (n: object) => boolean;
  addMark: (key: string, value: unknown) => void;
  removeMark: (key: string) => void;
  setNodes: (props: object, opts?: object) => void;
  insertNodes: (n: object) => void;
  getTransforms: (plugin: unknown) => Record<string, Record<string, (...a: unknown[]) => void>>;
  onChange: (...args: unknown[]) => void;
};

const useEditorMarkState = (editor: AnyEditor | null, key: string) => {
  const [active, setActive] = useState(false);
  const origRef = useRef<AnyEditor["onChange"] | null>(null);

  useEffect(() => {
    if (!editor) { setActive(false); return; }
    setActive(!!(editor.marks?.[key]));
    const original = editor.onChange;
    origRef.current = original;
    editor.onChange = (...args: unknown[]) => {
      original?.(...args);
      setActive(!!(editor.marks?.[key]));
    };
    return () => {
      if (origRef.current !== null) editor.onChange = origRef.current;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, key]);

  return active;
};

const useEditorBlockType = (editor: AnyEditor | null) => {
  const [type, setType] = useState("p");
  const origRef = useRef<AnyEditor["onChange"] | null>(null);

  useEffect(() => {
    if (!editor) { setType("p"); return; }
    const read = () => {
      try {
        const entries = Array.from(
          editor.nodes({ match: (n: { type?: unknown }) => typeof (n as { type?: unknown }).type === "string", mode: "highest" }),
        );
        const t = (entries[0]?.[0] as { type?: string })?.type;
        setType(t ?? "p");
      } catch { setType("p"); }
    };
    read();
    const original = editor.onChange;
    origRef.current = original;
    editor.onChange = (...args: unknown[]) => { original?.(...args); read(); };
    return () => { if (origRef.current !== null) editor.onChange = origRef.current; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  return type;
};

const useIsInTable = (editor: AnyEditor | null) => {
  const [inTable, setInTable] = useState(false);
  const origRef = useRef<AnyEditor["onChange"] | null>(null);

  useEffect(() => {
    if (!editor) { setInTable(false); return; }
    const check = () => {
      try {
        const entries = Array.from(
          editor.nodes({ match: (n: { type?: unknown }) => (n as { type?: string }).type === "table" }),
        );
        setInTable(entries.length > 0);
      } catch { setInTable(false); }
    };
    check();
    const original = editor.onChange;
    origRef.current = original;
    editor.onChange = (...args: unknown[]) => { original?.(...args); check(); };
    return () => { if (origRef.current !== null) editor.onChange = origRef.current; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  return inTable;
};

// ─── Toolbar Button ──────────────────────────────────────────────────────────

type TbBtnProps = {
  active?: boolean;
  title: string;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
};

const TbBtn: React.FC<TbBtnProps> = ({
  active,
  title,
  onClick,
  children,
  className = "",
  disabled,
}) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onMouseDown={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className={`inline-flex items-center justify-center w-7 h-7 rounded text-xs font-medium transition-colors
      ${active ? "bg-emerald-100 text-emerald-800" : "text-gray-700 hover:bg-gray-100"}
      ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}
      ${className}`}
  >
    {children}
  </button>
);

const Divider = () => <div className="w-px h-5 bg-gray-200 mx-0.5 self-center" />;

// ─── Block-type labels ────────────────────────────────────────────────────────

const BLOCK_LABELS: Record<string, string> = {
  p: "Text",
  h1: "Heading 1",
  h2: "Heading 2",
  h3: "Heading 3",
  blockquote: "Quote",
  hr: "Divider",
};

// ─── Main Toolbar ────────────────────────────────────────────────────────────

type ToolbarProps = { editor: AnyEditor | null };

export const Toolbar: React.FC<ToolbarProps> = ({ editor }) => {
  const isBold = useEditorMarkState(editor, "bold");
  const isItalic = useEditorMarkState(editor, "italic");
  const isUnderline = useEditorMarkState(editor, "underline");
  const isStrikethrough = useEditorMarkState(editor, "strikethrough");
  const blockType = useEditorBlockType(editor);
  const isInTable = useIsInTable(editor);
  const [fontSize] = useState("16");
  const [tableMenuOpen, setTableMenuOpen] = useState(false);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  // ── Mark toggles ──────────────────────────────────────────────────────────
  const toggleMark = useCallback(
    (key: string) => {
      if (!editor) return;
      const active = !!(editor.marks?.[key]);
      if (active) {
        editor.removeMark(key);
      } else {
        editor.addMark(key, true);
      }
    },
    [editor],
  );

  // ── Block-type set ────────────────────────────────────────────────────────
  const setBlockType = useCallback(
    (type: string) => {
      if (!editor) return;
      setTypeMenuOpen(false);
      try {
        switch (type) {
          case "h1": editor.getTransforms(H1Plugin).h1.toggle(); break;
          case "h2": editor.getTransforms(H2Plugin).h2.toggle(); break;
          case "h3": editor.getTransforms(H3Plugin).h3.toggle(); break;
          case "blockquote": editor.getTransforms(BlockquotePlugin).blockquote.toggle(); break;
          case "hr": editor.getTransforms(HorizontalRulePlugin).hr.toggle?.(); break;
          default:
            editor.setNodes({ type: "p" }, { match: (n: object) => editor.isBlock(n), mode: "highest" });
        }
      } catch { /* Silently skip unknown transforms */ }
    },
    [editor],
  );

  // ── Text alignment ────────────────────────────────────────────────────────
  const setAlign = useCallback(
    (align: "left" | "center" | "right") => {
      if (!editor) return;
      try {
        editor.getTransforms(TextAlignPlugin).textAlign.setNodes(align);
      } catch {
        editor.setNodes({ textAlign: align }, { match: (n: object) => editor.isBlock(n), mode: "highest" });
      }
    },
    [editor],
  );

  // ── Font size ─────────────────────────────────────────────────────────────
  const [displaySize, setDisplaySize] = useState(16);
  const adjustFontSize = useCallback(
    (delta: number) => {
      if (!editor) return;
      setDisplaySize((prev) => {
        const next = Math.min(96, Math.max(8, prev + delta));
        try {
          editor.getTransforms(FontSizePlugin).fontSize.addMark(`${next}px`);
        } catch {
          editor.addMark("fontSize", `${next}px`);
        }
        return next;
      });
    },
    [editor],
  );

  // ── Table insert + management ─────────────────────────────────────────────
  const insertTable = useCallback(() => {
    if (!editor) return;
    try {
      editor.getTransforms(TablePlugin).insert.table({ rowCount: 3, colCount: 3 });
    } catch {
      editor.insertNodes({
        type: "table",
        children: [
          { type: "tr", children: [0, 1, 2].map((c) => ({ type: "th", children: [{ type: "p", children: [{ text: `Header ${c + 1}` }] }] })) },
          ...[0, 1].map((r) => ({ type: "tr", children: [0, 1, 2].map((c) => ({ type: "td", children: [{ type: "p", children: [{ text: `R${r + 1}C${c + 1}` }] }] })) })),
        ],
      });
    }
  }, [editor]);

  const tableAction = useCallback((action: string) => {
    if (!editor) return;
    setTableMenuOpen(false);
    try {
      const tf = editor.getTransforms(TablePlugin) as {
        insert?: { tableRow?: () => void; tableColumn?: () => void };
        remove?: { tableRow?: () => void; tableColumn?: () => void; table?: () => void };
      };
      switch (action) {
        case "addRow":    tf.insert?.tableRow?.(); break;
        case "deleteRow": tf.remove?.tableRow?.(); break;
        case "addCol":    tf.insert?.tableColumn?.(); break;
        case "deleteCol": tf.remove?.tableColumn?.(); break;
        case "deleteTable": tf.remove?.table?.(); break;
      }
    } catch (e) {
      console.warn("[Toolbar] Table action failed:", e);
    }
  }, [editor]);

  // ── Color presets ─────────────────────────────────────────────────────────
  const COLORS = ["#1f2937", "#dc2626", "#d97706", "#16a34a", "#2563eb", "#9333ea", "#db2777", "#0891b2"];

  const setFontColor = useCallback(
    (color: string) => {
      if (!editor) return;
      editor.addMark("color", color);
      setColorPickerOpen(false);
    },
    [editor],
  );

  return (
    <div
      data-testid="plate-toolbar"
      className="flex flex-nowrap items-center gap-0.5 px-2 py-1.5 bg-white z-20"
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* Undo / Redo */}
      <TbBtn title="Undo (Ctrl+Z)" onClick={() => editor?.undo()} disabled={!editor}>
        <Undo2 className="h-3.5 w-3.5" />
      </TbBtn>
      <TbBtn title="Redo (Ctrl+Y)" onClick={() => editor?.redo()} disabled={!editor}>
        <Redo2 className="h-3.5 w-3.5" />
      </TbBtn>

      <Divider />

      {/* Block type dropdown */}
      <div className="relative">
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setTypeMenuOpen((o) => !o);
          }}
          className="inline-flex items-center gap-1 px-2 h-7 rounded text-xs text-gray-700 hover:bg-gray-100 border border-gray-200"
        >
          <Type className="h-3.5 w-3.5" />
          <span className="min-w-[48px] text-left">
            {BLOCK_LABELS[blockType] ?? "Text"}
          </span>
          <ChevronDown className="h-3 w-3" />
        </button>
        {typeMenuOpen && (
          <div className="absolute top-8 left-0 z-50 bg-white border border-gray-200 rounded shadow-lg py-1 w-36">
            {Object.entries(BLOCK_LABELS).map(([type, label]) => (
              <button
                key={type}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setBlockType(type);
                }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${
                  blockType === type ? "text-emerald-700 font-medium" : "text-gray-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <Divider />

      {/* Font size */}
      <TbBtn title="Decrease font size" onClick={() => adjustFontSize(-2)}>
        <Minus className="h-3 w-3" />
      </TbBtn>
      <span className="text-xs text-gray-600 w-7 text-center tabular-nums">
        {displaySize}
      </span>
      <TbBtn title="Increase font size" onClick={() => adjustFontSize(2)}>
        <Plus className="h-3 w-3" />
      </TbBtn>

      <Divider />

      {/* Marks */}
      <TbBtn title="Bold (Ctrl+B)" active={isBold} onClick={() => toggleMark("bold")}>
        <Bold className="h-3.5 w-3.5" />
      </TbBtn>
      <TbBtn title="Italic (Ctrl+I)" active={isItalic} onClick={() => toggleMark("italic")}>
        <Italic className="h-3.5 w-3.5" />
      </TbBtn>
      <TbBtn title="Underline (Ctrl+U)" active={isUnderline} onClick={() => toggleMark("underline")}>
        <Underline className="h-3.5 w-3.5" />
      </TbBtn>
      <TbBtn title="Strikethrough" active={isStrikethrough} onClick={() => toggleMark("strikethrough")}>
        <Strikethrough className="h-3.5 w-3.5" />
      </TbBtn>

      {/* Font color */}
      <div className="relative">
        <TbBtn
          title="Font color"
          onClick={() => setColorPickerOpen((o) => !o)}
        >
          <span className="text-xs font-bold" style={{ color: "#1f2937" }}>A</span>
        </TbBtn>
        {colorPickerOpen && (
          <div className="absolute top-8 left-0 z-50 bg-white border border-gray-200 rounded shadow-lg p-2 flex flex-wrap gap-1 w-24">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setFontColor(c);
                }}
                className="w-5 h-5 rounded-full border border-gray-200 hover:scale-110 transition-transform"
                style={{ background: c }}
                title={c}
              />
            ))}
          </div>
        )}
      </div>

      <Divider />

      {/* Alignment */}
      <TbBtn title="Align left" onClick={() => setAlign("left")}>
        <AlignLeft className="h-3.5 w-3.5" />
      </TbBtn>
      <TbBtn title="Align center" onClick={() => setAlign("center")}>
        <AlignCenter className="h-3.5 w-3.5" />
      </TbBtn>
      <TbBtn title="Align right" onClick={() => setAlign("right")}>
        <AlignRight className="h-3.5 w-3.5" />
      </TbBtn>

      <Divider />

      {/* Lists (keyboard shortcuts work natively; toolbar as shortcut) */}
      <TbBtn title="Bullet list" onClick={() => setBlockType("p")}>
        <List className="h-3.5 w-3.5" />
      </TbBtn>
      <TbBtn title="Numbered list" onClick={() => setBlockType("p")}>
        <ListOrdered className="h-3.5 w-3.5" />
      </TbBtn>
      <TbBtn title="Blockquote" active={blockType === "blockquote"} onClick={() => setBlockType("blockquote")}>
        <Quote className="h-3.5 w-3.5" />
      </TbBtn>

      <Divider />

      {/* Table — split button: click icon = insert, click ▾ = manage existing */}
      <div className="relative flex items-center">
        <TbBtn title="Insert table (3×3)" onClick={insertTable}>
          <TableIcon className="h-3.5 w-3.5" />
        </TbBtn>
        {isInTable && (
          <>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setTableMenuOpen((o) => !o);
              }}
              className="inline-flex items-center h-7 px-0.5 rounded-r text-gray-700 hover:bg-gray-100"
              title="Table options"
            >
              <ChevronDown className="h-3 w-3" />
            </button>
            {tableMenuOpen && (
              <div className="absolute top-8 left-0 z-50 bg-white border border-gray-200 rounded shadow-lg py-1 w-40">
                <button type="button" onMouseDown={(e) => { e.preventDefault(); tableAction("addRow"); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <PlusSquare className="h-3.5 w-3.5" /> Add Row Below
                </button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); tableAction("addCol"); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <Columns3 className="h-3.5 w-3.5" /> Add Column Right
                </button>
                <div className="my-1 border-t border-gray-100" />
                <button type="button" onMouseDown={(e) => { e.preventDefault(); tableAction("deleteRow"); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-red-50 flex items-center gap-2">
                  <RowsIcon className="h-3.5 w-3.5" /> Delete Row
                </button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); tableAction("deleteCol"); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-red-50 flex items-center gap-2">
                  <Columns3 className="h-3.5 w-3.5" /> Delete Column
                </button>
                <div className="my-1 border-t border-gray-100" />
                <button type="button" onMouseDown={(e) => { e.preventDefault(); tableAction("deleteTable"); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2 font-medium">
                  <Trash2 className="h-3.5 w-3.5" /> Delete Table
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Close any open dropdown when clicking outside */}
      {(typeMenuOpen || colorPickerOpen || tableMenuOpen) && (
        <div
          className="fixed inset-0 z-40"
          onMouseDown={() => {
            setTypeMenuOpen(false);
            setColorPickerOpen(false);
            setTableMenuOpen(false);
          }}
        />
      )}
    </div>
  );
};
